import type { ConfidenceGrade } from "@march/shared";
import { theme } from "../theme.ts";

/** How past forecasts for a SKU compared with what happened. */
export const GRADE_STYLE: Record<
  ConfidenceGrade,
  { label: string; bg: string; fg: string; border: string; help: string }
> = {
  high: {
    label: "High",
    bg: theme.color.successFaint,
    fg: theme.color.successText,
    border: theme.color.successBorder,
    help: "Most past forecasts landed within ±25% of what sold.",
  },
  medium: {
    label: "Medium",
    bg: theme.color.brandFaint,
    fg: theme.color.brandInk,
    border: theme.color.brandBorder,
    help: "Most past forecasts landed within ±50%, but not ±25%.",
  },
  low: {
    label: "Low",
    bg: theme.color.dangerFaint,
    fg: theme.color.dangerText,
    border: theme.color.dangerBorder,
    help: "Past forecasts were often off by more than 50%. Use judgement.",
  },
  low_volume: {
    label: "Low volume",
    bg: theme.color.surfaceHover,
    fg: theme.color.textMuted,
    border: theme.color.border,
    help: "Sells too few units for a forecast to mean much either way.",
  },
  new: {
    label: "New",
    bg: theme.color.surfaceHover,
    fg: theme.color.textMuted,
    border: theme.color.border,
    help: "Not enough past windows to grade yet.",
  },
};

export function ConfidenceBadge({
  grade,
  hitRate,
}: {
  grade: ConfidenceGrade | null | undefined;
  hitRate?: number | null;
}) {
  if (!grade) return <span style={{ color: theme.color.textFaint }}>—</span>;
  const g = GRADE_STYLE[grade];
  const rate =
    hitRate != null && (grade === "high" || grade === "medium" || grade === "low")
      ? ` ${Math.round(hitRate * 100)}% of past windows were within ±25%.`
      : "";
  return (
    <span
      title={g.help + rate}
      style={{
        padding: "1px 7px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        background: g.bg,
        color: g.fg,
        border: `1px solid ${g.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {g.label}
    </span>
  );
}
